import { Link, useParams } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useArchiveLoan, useGenerateSchedule, useLoan, useSchedule } from '@/features/loans/queries';
import { useProperty } from '@/features/properties/queries';
import { AmortizationChart } from '@/features/loans/AmortizationChart';
import { ScheduleTable } from '@/features/loans/ScheduleTable';
import { formatCents, formatIsoDate, formatRatio } from '@/lib/format';
import { apiErrorMessage } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function LoanDetailPage() {
  const { propertyId, loanId } = useParams({ strict: false }) as { propertyId: string; loanId: string };
  const propertyIdNum = Number(propertyId);
  const loanIdNum = Number(loanId);

  const property = useProperty(propertyIdNum);
  const loan = useLoan(propertyIdNum, loanIdNum);
  const schedule = useSchedule(propertyIdNum, loanIdNum);
  const archiveLoan = useArchiveLoan(propertyIdNum, loanIdNum);
  const generateSchedule = useGenerateSchedule(propertyIdNum, loanIdNum);

  if (loan.isPending || property.isPending) return <p className="text-muted-foreground">Chargement…</p>;
  if (loan.isError || !loan.data || property.isError || !property.data) {
    return <p className="text-destructive">Prêt introuvable.</p>;
  }

  const data = loan.data;
  const currency = property.data.currency;
  const archived = data.archivedAt !== null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/biens/$propertyId" params={{ propertyId }} className="text-sm text-muted-foreground hover:underline">
          ← Retour au bien
        </Link>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          {data.lender}
          {archived && <Badge variant="secondary">Archivé</Badge>}
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/biens/$propertyId/prets/$loanId/modifier" params={{ propertyId, loanId }}>
              Modifier
            </Link>
          </Button>
          <Button
            variant="outline"
            disabled={archiveLoan.isPending}
            onClick={() =>
              archiveLoan.mutate(!archived, {
                onSuccess: () => toast.success(archived ? 'Prêt rétabli' : 'Prêt archivé'),
                onError: (error) => toast.error(apiErrorMessage(error, "Échec de l'archivage")),
              })
            }
          >
            {archived ? 'Rétablir' : 'Archiver'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Conditions</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-4">
          <Info label="Capital" value={formatCents(data.principalCents, currency)} />
          <Info label="Taux nominal" value={formatRatio(data.annualRate)} />
          <Info label="Taux assurance" value={formatRatio(data.insuranceAnnualRate)} />
          <Info label="Durée" value={`${data.durationMonths} mois`} />
          <Info label="Déblocage" value={formatIsoDate(data.startDate)} />
        </CardContent>
      </Card>

      {!data.hasSchedule && (
        <Card>
          <CardHeader>
            <CardTitle>Échéancier</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-start gap-3">
            <p className="text-sm text-muted-foreground">
              L'échéancier n'est pas encore généré. Une fois généré, les conditions du prêt sont verrouillées.
            </p>
            <Button
              disabled={generateSchedule.isPending}
              onClick={() =>
                generateSchedule.mutate(undefined, {
                  onError: (error) => toast.error(apiErrorMessage(error, 'Échec de la génération')),
                })
              }
            >
              {generateSchedule.isPending ? 'Génération…' : "Générer l'échéancier"}
            </Button>
          </CardContent>
        </Card>
      )}

      {data.hasSchedule && schedule.isPending && <p className="text-muted-foreground">Chargement de l'échéancier…</p>}
      {data.hasSchedule && schedule.isError && (
        <p className="text-destructive">Impossible de charger l'échéancier. Rechargez la page pour réessayer.</p>
      )}

      {data.hasSchedule && schedule.data?.generated && schedule.data.totals && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Échéancier</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-6">
              <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-4">
                <Info
                  label="Mensualité (hors assurance)"
                  value={formatCents(schedule.data.monthlyPaymentCents ?? 0, currency)}
                />
                <Info label="Total capital" value={formatCents(schedule.data.totals.principalCents, currency)} />
                <Info label="Total intérêts" value={formatCents(schedule.data.totals.interestCents, currency)} />
                <Info label="Total assurance" value={formatCents(schedule.data.totals.insuranceCents, currency)} />
              </div>
              <AmortizationChart rows={schedule.data.rows} currency={currency} />
            </CardContent>
          </Card>

          <ScheduleTable rows={schedule.data.rows} currency={currency} />
        </>
      )}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}
